'use client';

import type { InsightTemplate } from '@/@studio-ai/types';
import {
    Button,
    Card,
    CardBody,
    Image,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    Spinner,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import NextImage from 'next/image';
import { useMemo, useState } from 'react';

const ALL_CATEGORY = '__all__';

function TemplateLibraryImage({ template }: { template: InsightTemplate }) {
    if (template.imageDataUri) {
        return (
            <Image
                src={template.imageDataUri}
                as={NextImage}
                alt={template.title}
                width={480}
                height={240}
                radius="none"
                removeWrapper
                classNames={{
                    img: 'w-full h-full object-cover',
                }}
            />
        );
    }
    return (
        <div className="flex aspect-[3/2] w-full items-center justify-center bg-default-100">
            <Icon icon="lucide:sparkles" width={32} height={32} className="text-default-500" />
        </div>
    );
}

export function TemplateLibraryModal({
    isOpen,
    onOpenChange,
    templates,
    onSelectTemplate,
    isSelecting = false,
    selectedTemplateId = null,
}: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    templates: InsightTemplate[];
    onSelectTemplate: (template: InsightTemplate) => Promise<void> | void;
    isSelecting?: boolean;
    selectedTemplateId?: number | null;
}) {
    const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORY);

    const categories = useMemo(() => {
        const seen = new Set<string>();
        const ordered: string[] = [];
        for (const t of templates) {
            const cat = (t.category ?? '').trim();
            if (cat && !seen.has(cat)) {
                seen.add(cat);
                ordered.push(cat);
            }
        }
        return ordered;
    }, [templates]);

    const filteredTemplates = useMemo(() => {
        if (activeCategory === ALL_CATEGORY) return templates;
        return templates.filter((t) => (t.category ?? '').trim() === activeCategory);
    }, [templates, activeCategory]);

    return (
        <Modal
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            size="full"
            scrollBehavior="inside"
            backdrop="opaque"
            classNames={{
                header: 'border-b border-default-100',
                body: 'px-4 py-6 sm:px-6',
            }}
        >
            <ModalContent>
                {() => (
                    <>
                        <ModalHeader className="flex flex-col gap-1 px-4 sm:px-6">
                            <div className="mx-auto flex w-full max-w-5xl flex-col gap-1">
                                <h3 className="text-large font-semibold text-foreground">
                                    Insights Template Library
                                </h3>
                                <p className="text-small font-normal text-default-500">
                                    Select a template below to start discovering insights.
                                </p>
                            </div>
                        </ModalHeader>

                        <ModalBody>
                            <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
                                <div className="flex flex-wrap gap-2">
                                    <CategoryPill
                                        label="All"
                                        isActive={activeCategory === ALL_CATEGORY}
                                        onPress={() => setActiveCategory(ALL_CATEGORY)}
                                    />
                                    {categories.map((cat) => (
                                        <CategoryPill
                                            key={cat}
                                            label={cat}
                                            isActive={activeCategory === cat}
                                            onPress={() => setActiveCategory(cat)}
                                        />
                                    ))}
                                </div>

                                {filteredTemplates.length === 0 ? (
                                    <div className="flex h-40 items-center justify-center rounded-medium border border-dashed border-default-200 text-small text-default-400">
                                        No templates in this category.
                                    </div>
                                ) : (
                                    <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                        {filteredTemplates.map((template) => (
                                            <Card
                                                key={template.id}
                                                fullWidth
                                                isPressable
                                                isDisabled={isSelecting}
                                                onPress={() => onSelectTemplate(template)}
                                                radius="lg"
                                                shadow="none"
                                                classNames={{
                                                    base: 'h-full overflow-hidden border border-default-200/60 bg-background hover:bg-mfour-green/10 transition-all hover:border-success-300 hover:shadow-md hover:ring-1 hover:ring-success-300/40 dark:bg-default-50/90',
                                                }}
                                            >
                                                <TemplateLibraryImage template={template} />
                                                <CardBody className="flex flex-col gap-1.5 p-4">
                                                    <span className="text-medium font-semibold text-foreground">
                                                        {template.title}
                                                    </span>
                                                    <span className="text-small leading-snug text-default-500">
                                                        {template.subtitle}
                                                    </span>
                                                    {isSelecting &&
                                                        selectedTemplateId === template.id && (
                                                            <div className="mt-2 flex items-center gap-2 text-tiny font-medium text-default-600">
                                                                <Spinner size="sm" />
                                                                <span>Starting template...</span>
                                                            </div>
                                                        )}
                                                </CardBody>
                                            </Card>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </ModalBody>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
}

function CategoryPill({
    label,
    isActive,
    onPress,
}: {
    label: string;
    isActive: boolean;
    onPress: () => void;
}) {
    return (
        <Button
            size="sm"
            radius="full"
            variant={isActive ? 'solid' : 'bordered'}
            color={isActive ? 'success' : 'default'}
            onPress={onPress}
            className={
                isActive
                    ? 'bg-mfour-green text-foreground-50 data-[hover=true]:bg-mfour-green/90'
                    : 'border-default-200 text-default-600 data-[hover=true]:bg-default-100'
            }
        >
            {label}
        </Button>
    );
}
